import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ClientContacts() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [contacts, setContacts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [search, setSearch] = useState('');

  // Create/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', mobile: '', position: '', department: '', contact_role_id: '', is_primary: false });
  const [saving, setSaving] = useState(false);

  const fetchContacts = () => {
    if (!companyId || !clientId) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.append('search', search);

    fetch(`${API}/companies/${companyId}/clients/${clientId}/contacts?${params}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setContacts(Array.isArray(d.data) ? d.data : []))
      .catch(() => setContacts([]))
      .finally(() => setLoading(false));
  };

  const fetchRoles = () => {
    fetch(`${API}/companies/${companyId}/clients-settings/contact-roles`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setRoles(Array.isArray(d) ? d : []))
      .catch(() => setRoles([]));
  };

  useEffect(() => { fetchContacts(); fetchRoles(); }, [companyId, clientId, search, API]);

  const openCreate = () => {
    setEditingContact(null);
    setForm({ first_name: '', last_name: '', email: '', phone: '', mobile: '', position: '', department: '', contact_role_id: '', is_primary: false });
    setShowModal(true);
  };

  const openEdit = (contact) => {
    setEditingContact(contact);
    setForm({
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      mobile: contact.mobile || '',
      position: contact.position || '',
      department: contact.department || '',
      contact_role_id: contact.contact_role_id || '',
      is_primary: contact.is_primary || false,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = editingContact
        ? `${API}/companies/${companyId}/clients/${clientId}/contacts/${editingContact.id}`
        : `${API}/companies/${companyId}/clients/${clientId}/contacts`;
      const res = await fetch(url, {
        method: editingContact ? 'PUT' : 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setShowModal(false);
      fetchContacts();
    } catch { alert('Failed to save contact'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await fetch(`${API}/companies/${companyId}/clients/${clientId}/contacts/${contactId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      fetchContacts();
    } catch { alert('Failed to delete'); }
  };

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;

  return (
    <div style={pageStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <button onClick={() => navigate(`/company/clients/${clientId}`)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', padding: '4px' }}>←</button>
        <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700 }}>👤 Contacts</h2>
        <button onClick={openCreate} style={btnPrimary}>+ Add Contact</button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <input
          placeholder="Search contacts..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={inputStyle}
        />
      </div>

      {initialLoading ? <p>Loading...</p> : (
        <div style={{ ...cardStyle, overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Phone</th>
                <th style={thStyle}>Position</th>
                <th style={thStyle}>Primary</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.length === 0 ? (
                <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No contacts found</td></tr>
              ) : contacts.map(c => (
                <tr key={c.id}>
                  <td style={tdStyle}><span style={{ fontWeight: 600 }}>{c.first_name} {c.last_name || ''}</span></td>
                  <td style={tdStyle}>{c.contact_role?.name || '—'}</td>
                  <td style={tdStyle}>{c.email || '—'}</td>
                  <td style={tdStyle}>{c.phone || c.mobile || '—'}</td>
                  <td style={tdStyle}>{c.position || '—'}</td>
                  <td style={tdStyle}>{c.is_primary ? '✅' : ''}</td>
                  <td style={tdStyle}>
                    <span style={{ ...badgeStyle, background: c.status === 'active' ? '#10b98120' : '#6b728020', color: c.status === 'active' ? '#10b981' : '#6b7280' }}>
                      {c.status}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => openEdit(c)} style={actionBtn}>✏️</button>
                      <button onClick={() => handleDelete(c.id)} style={actionBtn}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 700 }}>
              {editingContact ? 'Edit Contact' : 'Add Contact'}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>First Name *</label>
                <input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} style={modalInputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Last Name</label>
                <input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} style={modalInputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Email</label>
                <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={modalInputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Phone</label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={modalInputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Mobile</label>
                <input value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} style={modalInputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Position</label>
                <input value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} style={modalInputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Department</label>
                <input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} style={modalInputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Contact Role</label>
                <select value={form.contact_role_id} onChange={e => setForm({ ...form, contact_role_id: e.target.value })} style={modalInputStyle}>
                  <option value="">Select role...</option>
                  {roles.filter(r => r.is_active).map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="checkbox" checked={form.is_primary} onChange={e => setForm({ ...form, is_primary: e.target.checked })} />
                  Primary Contact
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={btnCancel}>Cancel</button>
              <button onClick={handleSave} disabled={saving || !form.first_name} style={btnPrimary}>
                {saving ? 'Saving...' : editingContact ? 'Update' : 'Add Contact'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = { padding: '24px', maxWidth: '1200px' };
const cardStyle = { background: '#fff', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' };
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#334155' };
const badgeStyle = { padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' };
const inputStyle = { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', minWidth: '250px' };
const btnPrimary = { padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' };
const btnCancel = { padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer' };
const actionBtn = { background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', padding: '2px 4px' };
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle = { background: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '600px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '4px' };
const modalInputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' };
